import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, RefreshCcw, AlertTriangle, Search, ArrowRightLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function PriceGroups() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [moveTo, setMoveTo] = useState("");
    const [busy, setBusy] = useState(false);
    const [stale, setStale] = useState(null);
    const [staleNote, setStaleNote] = useState(null);
    const [skipped, setSkipped] = useState(null);

    const load = async () => {
        const isFirstLoad = !data;
        if (isFirstLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const r = await api.get("/admin/price-groups");
            setData(r.data);
            const allIds = new Set();
            for (const t of r.data.tiers) for (const m of t.members) allIds.add(m.id);
            for (const m of r.data.unassigned.members) allIds.add(m.id);
            setSelected((prev) => new Set([...prev].filter((id) => allIds.has(id))));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            if (isFirstLoad) setLoading(false);
            else setRefreshing(false);
        }
    };

    useEffect(() => { load(); }, []);

    const buckets = useMemo(() => {
        if (!data) return [];
        return [...data.tiers, { ...data.unassigned, code: null }];
    }, [data]);

    const matches = (m) => {
        const needle = q.trim().toLowerCase();
        if (!needle) return true;
        return [m.name, m.email, m.role, m.company]
            .some((v) => (v || "").toLowerCase().includes(needle));
    };

    const toggle = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const apply = async () => {
        if (!moveTo) { toast.error("Pick a tier to move them to."); return; }
        if (selected.size === 0) { toast.error("Select at least one user."); return; }
        setBusy(true);
        setStale(null); setStaleNote(null); setSkipped(null);
        try {
            const r = await api.put("/admin/price-groups/assign",
                { user_ids: [...selected], tier: moveTo });
            const { updated, unchanged, stale_cart_lines, stale_check } = r.data;
            toast.success(`${updated} moved${unchanged ? `, ${unchanged} already there` : ""}.`);
            if (stale_check) setStaleNote(stale_check);
            else if (stale_cart_lines?.length) setStale(stale_cart_lines);
            setSelected(new Set());
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const reprice = async () => {
        const ids = [...new Set((stale || []).map((l) => l.user_id))];
        if (!ids.length) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/price-groups/reprice", { user_ids: ids });
            const { repriced, skipped: skippedLines, error } = r.data;
            if (error) toast.error(error);
            else toast.success(`${repriced} cart line(s) re-priced.`);
            if (skippedLines?.length) {
                toast.warning(`${skippedLines.length} line(s) skipped — see reasons below.`);
                setSkipped(skippedLines);
            } else {
                setSkipped(null);
            }
            setStale(null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="p-6 text-muted-foreground">Loading price groups…</div>;
    if (!data) return null;

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5" /> Price Groups
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Which price each Non-Organic user is billed on the Sales Cart.
                    </p>
                </div>
                <button onClick={load} disabled={busy || refreshing}
                        className="h-9 px-3 inline-flex items-center gap-1 rounded-md border text-sm">
                    <RefreshCcw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {data.organic_with_tier_count > 0 && (
                <div className="sc-card p-3 text-sm flex items-start gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                        {data.organic_with_tier_count} Organic user(s) carry a price tier.
                        It is ignored — tiers apply to Non-Organic users only.
                    </span>
                </div>
            )}

            <div className="sc-card p-3 flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="sc-input pl-9" placeholder="Search name, email, role, company…"
                           value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <span className="text-sm text-muted-foreground">{selected.size} selected</span>
                <select className="sc-input w-auto" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                    <option value="">Move to…</option>
                    {data.tiers.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
                <button onClick={apply} disabled={busy || !selected.size || !moveTo}
                        className="h-9 px-3 inline-flex items-center gap-1 rounded-md bg-foreground text-background text-sm disabled:opacity-40">
                    <ArrowRightLeft className="w-4 h-4" /> Apply
                </button>
            </div>

            {staleNote && (
                <div className="sc-card p-3 text-sm text-amber-700">
                    Could not check open carts for stale prices: {staleNote}. Their carts may still
                    hold the previous price.
                </div>
            )}

            {stale?.length > 0 && (
                <div className="sc-card p-3 space-y-2">
                    <div className="text-sm font-medium">
                        {stale.length} open cart line(s) still hold the previous price
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Cart lines are snapshots — changing a tier does not re-price a cart already
                        in progress.
                    </div>
                    <ul className="text-xs space-y-0.5">
                        {stale.map((l, i) => (
                            <li key={i}>
                                {l.email} · {l.item_code} · ₱{l.old_unit_price} → ₱{l.new_unit_price}
                                {l.price_override && " · manual override, will be skipped"}
                            </li>
                        ))}
                    </ul>
                    <button onClick={reprice} disabled={busy}
                            className="h-8 px-3 rounded-md bg-foreground text-background text-xs">
                        Re-price open carts
                    </button>
                </div>
            )}

            {skipped?.length > 0 && (
                <div className="sc-card p-3 space-y-2">
                    <div className="text-sm font-medium">
                        {skipped.length} line(s) skipped during re-price
                    </div>
                    <ul className="text-xs space-y-0.5">
                        {skipped.map((s, i) => (
                            <li key={i}>
                                {s.email} · {s.item_code} · {s.reason}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {buckets.map((b) => {
                    const members = b.members.filter(matches);
                    return (
                        <div key={b.code || "unassigned"} className="sc-card p-3 space-y-2">
                            <div>
                                <div className="font-medium text-sm">{b.label}</div>
                                <div className="text-xs text-muted-foreground">
                                    {b.code
                                        ? `Bills from SAP list ${b.price_list} — ${b.price_list_name}`
                                        : b.note}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {b.members.length} user(s)
                                </div>
                            </div>
                            {b.description && (
                                <div className="text-[11px] text-muted-foreground">{b.description}</div>
                            )}
                            <div className="divide-y max-h-72 overflow-auto">
                                {members.length === 0 && (
                                    <div className="text-xs text-muted-foreground py-3">
                                        {q ? "No match in this group." : "No users."}
                                    </div>
                                )}
                                {members.map((m) => (
                                    <label key={m.id}
                                           className="flex items-start gap-2 py-1.5 text-xs cursor-pointer">
                                        <input type="checkbox" className="mt-0.5"
                                               checked={selected.has(m.id)}
                                               onChange={() => toggle(m.id)} />
                                        <span>
                                            <span className="font-medium">{m.name || m.email}</span>
                                            {m.status !== "active" && (
                                                <span className="ml-1 text-amber-600">(inactive)</span>
                                            )}
                                            <br />
                                            <span className="text-muted-foreground">
                                                {m.email} · {m.role}{m.company ? ` · ${m.company}` : ""}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
